import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Star, Phone, ArrowRight, Search, Car, MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SearchScreen = () => {
  const [searchPhase, setSearchPhase] = useState('searching');
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Simulăm găsirea vulcanizărilor disponibile
  useEffect(() => {
    const mockServices = [
      {
        id: 1,
        name: "Auto Vulcanizare Rapid",
        distance: "2.5 km",
        estimatedTime: "15 min",
        price: 150,
        rating: 4.8,
        reviews: 156,
        phone: "0722123456"
      },
      {
        id: 2,
        name: "Vulcanizare Non-Stop",
        distance: "3.1 km",
        estimatedTime: "20 min",
        price: 130,
        rating: 4.6,
        reviews: 98,
        phone: "0723456789"
      }
    ];

    // Initial search state
    setTimeout(() => {
      setSearchPhase('finding');
      
      // First service found
      setTimeout(() => {
        setServices([mockServices[0]]);
        
        // Second service found
        setTimeout(() => {
          setServices(mockServices);
          setSearchPhase('complete');
        }, 1500);
      }, 2000);
    }, 2000);
  }, []);

  const SearchingState = () => (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
      <div className="w-20 h-20 bg-blue-500 rounded-full relative mb-8 flex items-center justify-center">
        <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-75"></div>
        <Search className="w-10 h-10 text-white relative z-10" />
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">
        Se caută vulcanizări disponibile...
      </h2>
      <p className="text-gray-600 text-center">
        {services.length > 0 
          ? `Am găsit ${services.length} vulcanizări până acum...`
          : 'Găsim specialiștii cei mai apropiați de tine'}
      </p>
    </div>
  );

  const ServiceCard = ({ service, isNew }) => (
    <div 
      className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4 ${
        isNew ? 'animate-fade-in' : ''
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
          <Car className="w-6 h-6 text-gray-600" />
        </div>
        
        <div className="flex-grow">
          <div className="flex justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{service.name}</h3>
              <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span>{service.distance}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>{service.estimatedTime}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-green-600">
                {service.price} RON
              </div>
              <div className="flex items-center mt-1">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="ml-1 text-gray-700">{service.rating}</span>
                <span className="text-gray-500 text-sm ml-1">
                  ({service.reviews})
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setSelectedService(service)}
              className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                selectedService?.id === service.id
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 text-white'
              }`}
            >
              {selectedService?.id === service.id ? 'Selectat' : 'Selectează'}
            </button>
            <a
              href={`tel:${service.phone}`}
              className="py-2 px-4 rounded-xl border border-gray-200"
            >
              <Phone className="w-5 h-5 text-gray-600" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Menu Button */}
      <div className="absolute top-4 right-4 z-10">
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 bg-white rounded-full shadow-md"
        >
          <MoreVertical className="w-6 h-6 text-gray-600" />
        </button>
        
        {/* Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1">
            <button 
              className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100"
              onClick={() => {
                // Handle cancel intervention
                setIsMenuOpen(false);
              }}
            >
              Anulează intervenția
            </button>
          </div>
        )}
      </div>

      {searchPhase === 'searching' || searchPhase === 'finding' ? (
        <SearchingState />
      ) : (
        <div className="p-4">
          <div className="max-w-xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">
              Vulcanizări Disponibile
            </h1>

            <div className="space-y-4">
              {services.map((service, index) => (
                <ServiceCard 
                  key={service.id} 
                  service={service}
                  isNew={index === services.length - 1}
                />
              ))}
            </div>

            {selectedService && (
              <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg">
                <div className="max-w-xl mx-auto">
                  <button
                    onClick={() => navigate('/tracking')}
                    className="w-full bg-blue-600 text-white py-4 rounded-xl font-medium hover:bg-blue-700 flex items-center justify-center"
                  >
                    Confirmă Selecția
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchScreen;